import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getShareLink, incrementShareLinkView, forkConversation } from '../services/api';
import { formatTimestamp } from '../utils/dateFormatters';
import PdfReportCard from './PdfReportCard';
import { touchScrollColumn } from './ui/scrollStyles';

interface ShareLinkData {
  shareLink: {
    shareLinkId: string;
    fromUserId: string;
    fromUserName: string;
    fromUserPhoto?: string;
    title?: string;
    createdAt: any;
    viewCount: number;
  };
  messages: Array<{
    messageId: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: any;
    files?: Array<{
      name: string;
      url?: string;
      driveId?: string;
    }>;
    pdfReports?: Array<{
      id: string;
      title: string;
      fileName: string;
      pageCount: number;
      sizeBytes: number;
      version: number;
      createdAt: string;
      driveLink?: string;
    }>;
  }>;
}

interface SharedLinkViewerProps {
  shareLinkId: string;
  onNavigateToSession?: (sessionId: string) => void;
}

const SharedLinkViewer: React.FC<SharedLinkViewerProps> = ({
  shareLinkId,
  onNavigateToSession,
}) => {
  const [shareData, setShareData] = useState<ShareLinkData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewTracked, setViewTracked] = useState(false);
  const [isForking, setIsForking] = useState(false);
  const [forkError, setForkError] = useState<string | null>(null);

  // Validate shareLinkId prop
  useEffect(() => {
    if (!shareLinkId || shareLinkId.trim().length === 0) {
      setError('Invalid share link ID');
      setLoading(false);
      return;
    }
  }, [shareLinkId]);

  const loadShareLink = useCallback(async () => {
    if (!shareLinkId || shareLinkId.trim().length === 0) return;

    setLoading(true);
    setError(null);

    let isMounted = true;

    try {
      const response = await getShareLink(shareLinkId);
      if (isMounted) {
        setShareData(response);
      }
    } catch (err: any) {
      console.error('Failed to load shared link:', err);
      if (isMounted) {
        if (err.message.includes('not found')) {
          setError('This share link does not exist or has been deleted.');
        } else if (err.message.includes('revoked')) {
          setError('This share link has been revoked by the creator.');
        } else if (err.message.includes('expired')) {
          setError('This share link has expired.');
        } else {
          setError(err.message || 'Failed to load shared conversation');
        }
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [shareLinkId]);

  useEffect(() => {
    loadShareLink();
  }, [loadShareLink]);

  // Track view after successful load with 2-second delay
  useEffect(() => {
    if (!shareData || error || loading || viewTracked) {
      return;
    }

    console.log('[SharedLinkViewer] Share link loaded successfully, will track view in 2 seconds');

    const timer = setTimeout(async () => {
      try {
        console.log('[SharedLinkViewer] Tracking view for share link:', shareLinkId);
        await incrementShareLinkView(shareLinkId);
        setViewTracked(true);
        console.log('[SharedLinkViewer] View tracked successfully');
      } catch (err) {
        console.error('[SharedLinkViewer] Failed to track view:', err);
        // Don't show error to user - this is not critical
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [shareData, error, loading, shareLinkId, viewTracked]);

  const handleForkConversation = async () => {
    if (isForking) return;

    // Clear any previous fork errors
    setForkError(null);
    setIsForking(true);

    try {
      console.log('[SharedLinkViewer] Forking conversation from share link:', shareLinkId);
      const result = await forkConversation(shareLinkId);

      if (result.success && result.newSessionId) {
        console.log('[SharedLinkViewer] Conversation forked successfully to session:', result.newSessionId);

        // Navigate to new session
        if (onNavigateToSession) {
          onNavigateToSession(result.newSessionId);
        }
      } else {
        throw new Error('Failed to fork conversation');
      }
    } catch (err: any) {
      console.error('[SharedLinkViewer] Failed to fork conversation:', err);
      // Set fork error (doesn't replace view, shows as banner)
      setForkError(err.message || 'Failed to continue conversation. Please try again.');
    } finally {
      setIsForking(false);
    }
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      // Height comes from the .full-viewport class (100vh→100dvh fallback pair)
      backgroundColor: 'var(--bg-app)',
    },
    header: {
      padding: '1.5rem 2rem',
      borderBottom: '1px solid var(--border-1)',
      backgroundColor: 'white',
      flexShrink: 0,
    },
    headerContent: {
      maxWidth: '900px',
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    headerInfo: {
      flex: 1,
      minWidth: 0,
    },
    headerTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: 'var(--fg-1)',
      marginBottom: '0.375rem',
    },
    headerMeta: {
      fontSize: '0.875rem',
      color: 'var(--fg-2)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      flexWrap: 'wrap',
    },
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'center',
    },
    button: {
      padding: '0.5rem 1rem',
      borderRadius: 'var(--radius-md)',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: 'none',
    },
    primaryButton: {
      backgroundColor: 'var(--accent)',
      color: 'white',
    },
    messagesContainer: {
      ...touchScrollColumn,
      padding: '2rem 0',
    },
    message: {
      maxWidth: '900px',
      margin: '0 auto 1.5rem auto',
      padding: '0 2rem',
    },
    messageRole: {
      fontSize: '0.75rem',
      fontWeight: 600,
      color: 'var(--fg-2)',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: '0.5rem',
    },
    messageContent: {
      fontSize: '0.9375rem',
      lineHeight: 1.6,
      color: 'var(--fg-1)',
    },
    fileAttachment: {
      marginTop: '0.75rem',
      padding: '0.75rem',
      backgroundColor: 'var(--bg-surface-3)',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border-1)',
    },
    fileLink: {
      color: 'var(--accent)',
      textDecoration: 'none',
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    loadingState: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontSize: '1rem',
      color: 'var(--fg-2)',
    },
    errorState: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      flexDirection: 'column',
      gap: '1rem',
      padding: '2rem',
    },
    errorText: {
      fontSize: '1rem',
      color: 'var(--danger)',
      textAlign: 'center',
    },
    forkErrorBanner: {
      maxWidth: '900px',
      margin: '1rem auto',
      padding: '1rem',
      backgroundColor: 'var(--danger-soft)',
      border: '1px solid var(--danger)',
      borderRadius: 'var(--radius-md)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
    },
    forkErrorText: {
      fontSize: '0.875rem',
      color: 'var(--danger)',
      flex: 1,
    },
    forkErrorClose: {
      background: 'none',
      border: 'none',
      color: 'var(--danger)',
      cursor: 'pointer',
      fontSize: '1.25rem',
      padding: '0.25rem',
      lineHeight: 1,
    },
    emptyState: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '4rem 2rem',
      textAlign: 'center',
      color: 'var(--fg-2)',
      fontSize: '0.9375rem',
    },
  };

  if (loading) {
    return <div style={styles.loadingState}>Loading shared conversation...</div>;
  }

  if (error) {
    return (
      <div style={styles.errorState}>
        <div style={styles.errorText}>{error}</div>
      </div>
    );
  }

  if (!shareData) {
    return (
      <div style={styles.errorState}>
        <div style={styles.errorText}>Failed to load shared conversation</div>
      </div>
    );
  }

  const { shareLink, messages } = shareData;

  return (
    <div className="full-viewport" style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerInfo}>
            <div style={styles.headerTitle}>
              {shareLink.title || 'Shared Conversation'}
            </div>
            <div style={styles.headerMeta}>
              <span>Shared by {shareLink.fromUserName}</span>
              <span>•</span>
              <span>{formatTimestamp(shareLink.createdAt)}</span>
              <span>•</span>
              <span>{messages.length} messages</span>
            </div>
          </div>
          <div style={styles.headerActions}>
            <button
              style={{ ...styles.button, ...styles.primaryButton }}
              onClick={handleForkConversation}
              disabled={isForking}
            >
              {isForking ? 'Creating...' : 'Continue This Conversation'}
            </button>
          </div>
        </div>
      </div>

      {/* Fork Error Banner */}
      {forkError && (
        <div style={styles.forkErrorBanner}>
          <div style={styles.forkErrorText}>
            {forkError}
          </div>
          <button
            style={styles.forkErrorClose}
            onClick={() => setForkError(null)}
            aria-label="Close error"
          >
            ×
          </button>
        </div>
      )}

      <div style={styles.messagesContainer}>
        {messages.length === 0 ? (
          <div style={styles.emptyState}>
            No messages found in this shared conversation.
          </div>
        ) : (
          messages.map((message, index) => (
            <div key={message.messageId || index} style={styles.message}>
              <div style={styles.messageRole}>
                {message.role === 'user' ? 'User' : 'Culture Genius'}
              </div>
              <div style={styles.messageContent} className="markdown-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    table: ({ node, children, ...props }) => (
                      <div className="table-wrapper">
                        <table className="markdown-table" {...props}>{children}</table>
                      </div>
                    ),
                    thead: ({ node, children, ...props }) => (
                      <thead className="markdown-thead" {...props}>{children}</thead>
                    ),
                    tbody: ({ node, children, ...props }) => (
                      <tbody className="markdown-tbody" {...props}>{children}</tbody>
                    ),
                    tr: ({ node, children, ...props }) => (
                      <tr className="markdown-tr" {...props}>{children}</tr>
                    ),
                    th: ({ node, children, ...props }) => (
                      <th className="markdown-th" {...props}>{children}</th>
                    ),
                    td: ({ node, children, ...props }) => (
                      <td className="markdown-td" {...props}>{children}</td>
                    )
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>
              {message.files && message.files.length > 0 && (
                <div>
                  {message.files.map((file, fileIndex) => (
                    <div key={fileIndex} style={styles.fileAttachment}>
                      {file.url ? (
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={styles.fileLink}
                        >
                          📎 {file.name}
                        </a>
                      ) : (
                        <span style={{ fontSize: '0.875rem' }}>📎 {file.name}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {message.pdfReports && message.pdfReports.length > 0 && (
                <div>
                  {message.pdfReports.map((report, reportIndex) => (
                    <PdfReportCard key={report.id || reportIndex} report={report} readOnly />
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SharedLinkViewer;
